<template>
	<div id="app" class="order-online order-online-page">
		<section class="page-content page-tab">
			<div
				v-if="!userIsLoggedIn && !userIsGuest"
				class="order-online-block order-online-block--login-panel"
			>
				<LoginPanel
					@user_clicked_guest="userClickedGuest"
					@user_logged_in="userLoggedIn"
					:forgot_pw_url="store.links.forgot_password"
					:show_pickup="showPickup"
					:show_delivery="showDelivery"
				/>
			</div>

			<div
				v-else
				class="order-online-block order-online-block--home"
			>
				<div class="container">
					<div
						v-if="introText.length || introImg.length"
						class="row"
					>
						<div class="col-xxs-12">
							<div class="store-intro push-bottom soft-bottom">
								<div 
									v-if="introImg.length"
									v-html="introImg"
									class="store-intro__img"
								></div>
								<div
									v-if="introText.length"
									v-html="introText"
									class="store-intro__text"
								></div>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col-xxs-12">
							<Tabs
								@tab_click="handleTabClick"
								:tabs="tabs"
								:cart_items="cart.items"
							/>
						</div>
					</div>

					<div class="row">
						<div class="col-xxs-12">
							<section class="steps">
								<article
									:class="{ 'is-active': 'Get Started' === activeTab }"
									class="step"
								>
									<GetStarted
										@guest_wants_to_login="guestWantsToLogin"
										@user_selected_order_type="handleUserSelectedOrderType"
										@user_selected_location="handleUserSelectedLocation"
										@go_to_select_items="goToStep"
										:google_api_key="store.geo.googleApiKey"
										:is_fetching_ordertypes="store.isFetchingOrderTypes"
										:locations="store.geo.locations"
										:messages="store.messages"
										:my_account_url="store.links.my_account"
										:order_types="store.orderTypes"
										:user="user"
									/>
								</article>

								<article
									:class="{ 'is-active': 'Select Items' === activeTab }"
									class="step"
								>
									<SelectItems
										@finished_updating_cart="goToCart"
										@go_to_cart="goToCart"
										@go_to_select_items="goToSelectItems"
										@populate_cart_items="populateCartItems"
										@set_active_product="setActiveProduct"
										@set_active_screen="setActiveScreen"
										:active_product="store.activeProduct"
										:active_screen="store.activeScreen"
										:categories="store.categories"
										:extras="store.extras"
										:key="store.activeProductKey"
									/>
								</article>

								<article
									:class="{ 'is-active': 'Review Cart' === activeTab }"
									class="step"
								>
									<ReviewCart
										@edit_cart_item="editCartItem"
										@go_to_step="goToStep"
										@populate_cart_items="populateCartItems"
										@remove_cart_item="removeCartItem"
										:cart_items="cart.items"
										:extras="store.extras"
										:is_fetching_items="cart.isFetchingItems"
										:messages="store.messages"
										:order_types="store.orderTypes"
										:user_ordertype="getActiveOrderType"
									/>
								</article>
							</section>
						</div>
					</div>

				</div>
			</div>
		</section>
	</div>
</template>


<script>
	import WpApi from './helpers/WpApi.mjs'
	import CookieApi from './helpers/CookieApi.mjs'

	import LoginPanel from './views/LoginPanel.vue'
	import Tabs from './components/Tabs.vue'

	// ideally, if time allows, get router set up for these
	import GetStarted from './views/GetStarted.vue'
	import ReviewCart from './views/ReviewCart.vue'
	import SelectItems from './views/SelectItems.vue'


	export default {
		name: 'Store',
		components: {
			LoginPanel,
			GetStarted,
			ReviewCart,
			SelectItems,
			Tabs,
		},


		data() {
			return {
				tabs: [
					{
						name: 'Get Started',
						isActive: true,
					},
					{
						name: 'Select Items',
						isActive: false
					},
					{
						name: 'Review Cart',
						isActive: false,
					},
					{
						name: 'Checkout',
						isActive: false,
					},
				],

				store: {
					activeProductKey: Date.now(),
					activeProduct: null,
					activeScreen: 'category',

					// will be extracted from appData prop
					categories: {},	// includes products in category
					extras: {}, // for Review Cart
					links: {},
					messages: {},
					geo: {
						googleApiKey: '',
						locations: []
					},

					// todo — move Penn Med-specific messaging?
					pennMed: {
						logo: null,
					},

					orderTypes: {
						delivery: { // Penn Med only
							hasDelivered: false, // i.e. delivery already delivered for today
							isEnabled: false,
							isSelected: false,
							nextDeliveryDate: null,
						},
						pickup: {
							isEnabled: false,
							isOpen: false,
							isSelected: false,
						},
					},
					isFetchingOrderTypes: true, // used for loading indicator on order type widget
				},

				// will be extracted from WpApi.getUserStatus prop
				user: {
					displayName: '',
					isGuest: false,
					isPennMed: false,
				},

				cart: {
					isFetchingItems: true,
					isPennMed: false,
					items: []
				},
			}
		},



		computed: {

			/**
			 * Check if user is logged in
			 * 
			 * @return	{bool}	True, if user is logged in
			 */
			userIsLoggedIn() {
				return !!this.user.displayName.length
			},


			/**
			 * Check if user is a guest
			 * 
			 * @return	{bool}	True, if user is guest
			 */
			userIsGuest() {
				return this.user.isGuest
			},


			/**
			 * Check if user is a Penn Med user
			 * 
			 * @return	{bool}	True, if user is Penn Med
			 */
			userIsPennMed() {
				return this.user.isPennMed
			},


			/**
			 * Check if delivery is enabled and is an available option
			 * 
			 * @return	{bool}	True, if available
			 */
			showDelivery() {
				return this.store.orderTypes.delivery.isEnabled
			},


			/**
			 * Check if pickup is enabled and is an available option
			 * 
			 * @return	{bool}	True, if available
			 */
			showPickup() {
				return this.store.orderTypes.pickup.isEnabled
			},	
			

			/**
			 * Check if store is open (at least one order type needs to be available)
			 * 
			 * @return	{bool} 	True, if store is open
			 */
			storeIsOpen() {
				return this.showDelivery || this.showPickup
			},			


			/**
			 * Get name for active tab
			 * 
			 * @return	{string}	Active tab name
			 */
			activeTab() {
				const activeTab = this.tabs.find( tab => {
					return tab.isActive 
				})

				return activeTab ? activeTab.name : ''
			},


			/**
			 * Get introduction text to display above tabs
			 * 
			 * @return	{string} 	Intro text
			 */
			introText() {
				let text = ''

				const delivery = this.store.orderTypes,
					messages = this.store.messages

				// only show messages for Penn Med users
				if( this.userIsPennMed ) {

					// check if delivery as an order type is enabled
					if( !delivery.isEnabled ) {
						text = messages.delivery_inactive

					// otherwise, check if delivery cutoff time has passed
					} else {
						if( delivery.hasDelivered ) {
							text = messages.penn_med_intro_closed
						} else {
							text = messages.penn_med_intro
						}
					}
				}

				return text
			},


			/**
			 * Get introduction image to display above tabs
			 * 
			 * @return	{string}	Image source
			 */
			introImg() {
				let img = ''

				if( this.userIsPennMed && null !== this.store.pennMed.logo ) {
					img = this.store.pennMed.logo
				}

				return img
			},
		},



		methods: {
			handleTabClick( tab ) {
				if( 'object' === typeof tab && Object.prototype.hasOwnProperty.call( tab, 'name' ) ) {
					tab = tab.name
				}

				if( 'string' === typeof tab ) {
					this.goToStep( tab )
				}
			},


			goToSelectItems() {
				this.goToStep( 'Select Items' )
			},


			goToCart() {
				this.goToStep( 'Review Cart' )
			},


			goToStep( step, scrollToView = false ) {
				if( 'Select Items' === step ) {
					this.store.activeScreen 	= 'category'
					this.store.activeProductKey = Date.now() // resets current product

				// checkout is on a dedicated page
				} else if( 'Checkout' === step ) {
					window.location.href = this.store.links.checkout
					return
				}

				// optionally scroll to top of app
				if( scrollToView ) {
					window.scroll({
						top: this.$el.offsetTop
					})
				}

				// display active tab
				for( const tab of this.tabs ) {
					tab.isActive = ( step === tab.name )
				}
			},			


			setActiveScreen( screen ) {
				this.store.activeScreen = screen
			},


			parseUserStatus( status ) {

				// defaults
				let {
					displayName = '',
					isGuest 	= false,
					isLoggedIn 	= false,
					isPennMed 	= false,
					locationId 	= null,
					ordertype 	= ''
				} = status

				if( locationId ) {
					this.setActiveLocation( locationId )
				}

				// hides login panel on page load; remove to restore login panel
				if( !isLoggedIn ) {
					isGuest = true
				}

				this.user = {
					displayName,
					isGuest,
					isLoggedIn,
					isPennMed,
					ordertype
				}
			},			


			userClickedGuest() {
				CookieApi.setGuestCookie()

				this.user.isGuest = true
			},


			userLoggedIn( data ) {

				// refresh nonces
				WpApi.refreshNonces()
					.then( () => {
					
						// once nonces are set, update frontend
						this.user.isLoggedIn = data.is_logged_in
						this.user.isPennMed = data.is_penn_med
						this.user.displayName = data.user_name
		
						// update items in current cart
						this.getCartItems()
					})
			},


			guestWantsToLogin() {
				CookieApi.removeGuestCookie()

				this.user.isGuest = false
			},


			handleUserSelectedOrderType( orderType ) {
				if( this.setActiveOrderType( orderType ) ) {
					
					// updates cookie
					WpApi.setUserOrderType( orderType )
				}
			},


			handleUserSelectedLocation( locationId ) {
				if( this.setActiveLocation( locationId ) ) {
					
					// updates cookie
					WpApi.setUserLocationId( locationId )
				}
			},	
			
			
			parseOrderTypes( orderTypes ) {

				// determine if store is currently accepting delivery orders
				if( undefined !== orderTypes.delivery ) {
					const storeDelivery = this.store.orderTypes.delivery

					storeDelivery.isEnabled = orderTypes.delivery.enabled
					if( storeDelivery.isEnabled ) {
						storeDelivery.hasDelivered = orderTypes.delivery.delivery_closed

						/**
						 * Messaging SHOULD already have next delivery date (see FxOnlineStore\Messaging), but just in 
						 * case we need to reference
						 */ 
						storeDelivery.nextDeliveryDate = orderTypes.delivery.next_delivery_date
					}
				}

				// determine if store is currently accepting pickup orders
				if( undefined !== orderTypes.pickup ) {
					const storePickup = this.store.orderTypes.pickup

					storePickup.isEnabled = orderTypes.pickup.enabled
					if( storePickup.isEnabled ) {
						storePickup.isOpen = orderTypes.pickup.is_open
					}

					// if delivery is not selectable, automatically select pickup
					if( !this.showDelivery ) {
						storePickup.isSelected = true

						// updates cookie
						WpApi.setUserOrderType( 'pickup' )
					}
				}

				// if delivery is open, continually check with server that delivery is still available
				if( this.showDelivery && !this.store.orderTypes.delivery.hasDelivered ) {
					console.info( 'Pinging server for updated store status' )
					this.pingForDeliveryCloseTime( false )
				}

				this.store.isFetchingOrderTypes = false
			},			


			getActiveOrderType() {
				for( const [ orderTypeName, details ] of Object.entries( this.store.orderTypes ) ) {
					if( details.isEnabled && details.isSelected ) {
						return orderTypeName
					}
				}

				return ''
			},


			setActiveOrderType( selectedOrderTypeName ) {
				for( const [ orderTypeName, details ] of Object.entries( this.store.orderTypes ) ) {
					if( details.isEnabled ) {
						details.isSelected = ( orderTypeName === selectedOrderTypeName )
					}
				}

				return this.getActiveOrderType()
			},


			getActiveLocation( idOnly = false ) {
				const activeLocation = this.store.geo.locations.find( location => location.isSelected )

				if( activeLocation ) {
					return idOnly ? activeLocation.id : activeLocation
				}
			},


			setActiveLocation( locationId ) {
				this.store.geo.locations.forEach( location => {
					location.isSelected = ( locationId === location.id )
				})

				return this.getActiveLocation( true )
			},


			setActiveProduct( product ) {
				this.store.activeProduct = product
			},

			populateCartItems( cartItems ) {
				cartItems = cartItems.sort( ( a, b ) => {
					if( !a.is_extra && !b.is_extra ) {
						return 0
					} else if( !a.is_extra ) {
						return -1
					} else if( !b.is_extra ) {
						return 1
					}
				})

				this.cart.items = cartItems
			},


			async getCartItems() {
				this.cart.isFetchingItems = true

				const cartItems = await WpApi.getUserCart()

				this.populateCartItems( cartItems )
				this.cart.isFetchingItems = false
				
				return cartItems
			},


			removeCartItem( cartItemKey ) {
				this.cart.items = this.cart.items.filter( item => item.cart_item_key !== cartItemKey )
			},


			editCartItem( cartItem, scrollToView ) {
				this.store.activeProduct = cartItem
				this.store.activeScreen = 'active-product'

				// force-renders active product (otherwise, view won't change)
				this.store.activeProductKey = Date.now()

				if( scrollToView ) {
					window.scroll({
						top: this.$el.offsetTop
					})
				}

				this.$nextTick( () => this.goToStep( 'Select Items' ) )
			},


			pingForDeliveryCloseTime( useTimeout = true ) {
				const self = this,
					toWait = useTimeout ? 3e+4 : 0

				// ping server for updated delivery status every 30 seconds
				setTimeout( () => {
					WpApi
						.checkDeliveryClosedStatus()
						.then( deliveryHasClosed => {
							if( deliveryHasClosed ) {
								self.store.orderTypes.delivery.hasDelivered = true
							} else {
								self.pingForDeliveryCloseTime()
							}
						})
				}, toWait )
			}
		},



		created() {

			// grabs data from wp_localize_script
			const appData = WpApi.extractAppData()

			// passed from main.js (extracted via WpApi)
			this.store.categories = appData.categories
			this.store.extras = appData.extras
			this.store.geo.googleApiKey = appData.googleApiKey
			this.store.geo.locations = appData.locations
			this.store.links = appData.links
			this.store.messages = appData.messages
			this.store.pennMed.logo = appData.pennMedLogo
			this.user = appData.user

			// re-organize store status data for easier usage later
			this.parseOrderTypes( appData.storeStatus )

			// double-check user status by pulling from WP
			WpApi
				.getUserStatus()
				.then( response => this.parseUserStatus( response ) )
		},



		beforeMount() {

			// check if user is logged in. If not, user needs to login to proceed
			if( !this.user.isGuest && !this.user.isLoggedIn ) {
				this.store.loginIsVisible = true
			}

			/**
			 * Double-checks store  
			 * 
			 * Originally, when Kinsta was caching this page, we needed to ping the server to confirm the store's status
			 * to avoid the page showing the store was open when it was actually closed.
			 * 
			 * Starting 2021/05/11, Kinsta was directed to no longer cache this page. However, if Kinsta ever resumes 
			 * caching, we'll need to re-enable this.
			 */
			// WpApi
			// 	.getStoreStatus()
			// 	.then( response => this.parseOrderTypes( response ) )
		},



		async mounted() {

			// check if user has preexisting items in cart
			if( !this.cart.items.length ) {
				const cartItems = await this.getCartItems()

				// check if user's trying to edit a preexisting cart item
				const params = new URLSearchParams( window.location.search ),
					cartItemId = params.get( 'cart_item_id' )				

				// loop through cart items to see if we can find the 
				if( null !== cartItemId ) {
					const targetItem = cartItems.find( cartItem => cartItemId === cartItem.cart_item_key )

					if( undefined !== targetItem ) {
						this.editCartItem( targetItem )
					}
				}
			}

			// remove loading animation from app container
			const elContainer = document.getElementById('app-container')
			if( null !== elContainer ) {
				elContainer.classList.remove( 'is-loading' )
			}
		},


	}
</script>